import {getStyleSheets} from 'utils';
import {initWrapper, destroyWrapper, getWrapperId, getWrapperNode} from 'init';
import {VWRAP_KMC_KEY, evaluate, parseOptions} from './options';
import {loadElementListManifest, loadCaptionsManifest} from './element-list';

const getDOMNodeAsync = selector => new Promise(resolve => {
    let count = 0;
    const check = () => {
        const node = document.querySelector(selector);
        if (node) return resolve(node);
        if (++count < 1000) return setTimeout(check, 1);
        return resolve(null);
    };
    check();
});

const checkStylesheet = id => new Promise(resolve => {
    let count = 0;
    const check = () => {
        const styleSheets = getStyleSheets();
        for (let i = 0; i < styleSheets.length; i++) {
            if (styleSheets[i].ownerNode.id === id) return resolve();
        }
        if (++count < 1000) return setTimeout(check, 1);
        return resolve(null);
    };
    check();
});

const ON_PAGE_KMC_EMBED_JS_KEY = 'onPageJs1';
const IFRAME_KMC_EMBED_JS_KEY = 'IframeCustomPluginJs1';

// TODO: Research if there is another way to detect this more explicitly
export const useKalturaWrapper = () => Boolean(window.kWidget);

const getKMSVideoNode = kdp => kdp.closest('#wrapper.video');
const getIframeVideoNode = kdp => {
    if (kdp.playlist) return kdp.closest('.playlistInterface');
    return kdp.closest('.mwPlayerContainer');
};

const CUSTOM_STYLESHEET_IDS = {
    kms: `${VWRAP_KMC_KEY}-KMSFix`,
    iframe: `${VWRAP_KMC_KEY}-IframeFix`,
};

const removeStylesheet = stylesheetNode => {
    if (!stylesheetNode) return;
    stylesheetNode.disabled = true;
    stylesheetNode.parentNode.removeChild(stylesheetNode);
};

const insertCustomKMSCSS = () => {
    const styles = `
        #mediaContainer #wrapper.video {
            margin-left: 0;
            margin-right: 0;
        }
        .cielo24-vwrap {
            margin-bottom: 20px;
            text-shadow: none !important;
        }
        .cielo24-vwrap .toggle {
            margin-left: 0;
        }
    `;

    const id = CUSTOM_STYLESHEET_IDS.kms;
    const stylesheet = document.getElementById(id);
    if (stylesheet) removeStylesheet(stylesheet);

    const tag = document.createElement('style');
    tag.id = id;
    tag.type = 'text/css';
    tag.innerHTML = styles;
    document.head.appendChild(tag);

    return checkStylesheet(id);
};

const insertCustomIframeCSS = async (kdp, entry) => {
    // Calculate video height based on original video size and aspect ratio
    let ratio = entry.height / entry.width; // Landscape
    if (entry.width < entry.height) ratio = 1 / ratio; // Portrait

    const videoHeight = window.innerWidth * ratio;
    let videoContainerHeight = videoHeight;

    const videoControls = await getDOMNodeAsync('.controlBarContainer');
    if (videoControls) videoContainerHeight += videoControls.offsetHeight;

    let styles = `
        .mwPlayerContainer {
            height: ${videoContainerHeight}px !important;
        }
        .mwPlayerContainer .videoHolder {
            height: ${videoHeight}px !important;
        }
    `;

    if (kdp.playlist) {
        let containerHeight = videoContainerHeight;

        const playlistControls = await getDOMNodeAsync('.playlistAPI');
        const handlePlaylistHeight = playlistControls && playlistControls.style.position !== 'absolute';
        if (handlePlaylistHeight) containerHeight += playlistControls.offsetHeight;

        styles += `
            .playlistInterface {
                height: ${containerHeight}px !important;
            }
        `;

        // Update height of vertical playlist so it doesn't go over the videowrapper
        const verticalPlaylistHeader = await getDOMNodeAsync('.k-medialist-header.k-vertical');
        if (verticalPlaylistHeader) {
            styles += `
                .k-chapters-container {
                    height: ${containerHeight - verticalPlaylistHeader.offsetHeight}px !important;
                }
            `;
        }
    }

    const id = CUSTOM_STYLESHEET_IDS.iframe;
    const stylesheet = document.getElementById(id);
    if (stylesheet) removeStylesheet(stylesheet);

    const tag = document.createElement('style');
    tag.id = id;
    tag.type = 'text/css';
    tag.innerHTML = styles;
    document.head.appendChild(tag);

    return checkStylesheet(id);
};

const removeCustomStylesheets = () => Object.values(CUSTOM_STYLESHEET_IDS).forEach(id => {
    const stylesheet = document.getElementById(id);
    if (stylesheet) removeStylesheet(stylesheet);
});

const handleIframeResize = (entry, api) => insertCustomIframeCSS(entry, api);

const addKDPListener = (kdp, event, callbak) => {
    let binder = kdp.kBind;
    if (!binder) binder = kdp.addJsListener;
    if (binder) binder(event, callbak);
};

const WRAPPER_CACHE = {};
const addWrapperToCache = options => WRAPPER_CACHE[getWrapperId(options)] = options;
const destroyWrappersFromCache = () => (
    Object.entries(WRAPPER_CACHE).forEach(([wrapperId, options]) => {
        const containerNode = getWrapperNode(wrapperId);
        if (containerNode) {
            destroyWrapper(containerNode, options);
            Object.entries(options.eventHandlers).forEach(([event, handler]) => (
                window.removeEventListener(event, handler)
            ));
        }
    })
);

export const initKalturaWrapper = () => new Promise((resolve, reject) => {
    // TODO: Review if multiple non-iframe embeds are supported
    window.kWidget.addReadyCallback(playerId => { // Wait for player
        const kdp = document.getElementById(playerId); // Get player / kdp api
        if (!kdp) reject('Kaltura Player API failed to load');

        addKDPListener(kdp, 'playerError', () => reject('Kaltura Player initialization failed'));
        addKDPListener(kdp, 'entryReady', async (entry) => {
            // Directly return the player to avoid video node
            const kmsEmbed = Boolean(window.kmsEnable); // eslint-disable-line no-unused-vars
            const onPageEmbed = Boolean(evaluate(kdp, ON_PAGE_KMC_EMBED_JS_KEY)); // eslint-disable-line no-unused-vars
            const iframeEmbed = Boolean(evaluate(kdp, IFRAME_KMC_EMBED_JS_KEY)); // eslint-disable-line no-unused-vars

            const eventHandlers = {};

            let videoNode = kdp;
            if (kmsEmbed) videoNode = getKMSVideoNode(kdp);
            if (iframeEmbed) videoNode = getIframeVideoNode(kdp);

            const elementListManifest = await loadElementListManifest({kdp, entry});
            const captionsManifest = await loadCaptionsManifest({kdp, entry});

            if (!elementListManifest) {
                removeCustomStylesheets(); // Remove custom stylesheets
                destroyWrappersFromCache(); // Remove previous wrappers
                return;
            }

            const options = {
                ...parseOptions(kdp), // Add UI Variable options
                playerId,
                provider: 'kaltura',
                embedType: `Kaltura / ${iframeEmbed ? 'Iframe' : 'On-Screen'} Embed`,
                videoId: entry.id,
                apiNode: kdp,
                videoNode,
                elementListManifest,
                captionsManifest,
            };

            if (kmsEmbed) await insertCustomKMSCSS();

            if (iframeEmbed) {
                options.expandHeight = true;

                await insertCustomIframeCSS(kdp, entry);

                const resizeHandler = () => handleIframeResize(kdp, entry);
                eventHandlers.resize = resizeHandler;
                window.addEventListener('resize', resizeHandler);
            }

            // Handle playlists
            destroyWrappersFromCache();
            addWrapperToCache({...options, eventHandlers});

            resolve(initWrapper(options));
        });
    });
});



// WEBPACK FOOTER //
// ./scripts/adapters/kaltura/entry-point.js